import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Activity,
  Eye,
  ShieldCheck,
  X,
} from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatDate, formatDateTime, formatNumber } from '../../lib/format'
import { Breadcrumb } from '../../components/Breadcrumb'
import { Avatar } from '../../components/Avatar'
import { FilterRow } from '../../components/filters/FilterRow'
import { DataTable } from '../../components/table/DataTable'
import { RowActionsMenu } from '../../components/table/RowActionsMenu'
import { auditLogResponse } from '../../lib/types'
import {
  moduleOptions,
  actionOptions,
  actionLabel,
  actionToneClasses,
  actionKeysMatching,
  describeAuditRow,
  moduleDescription,
  CA_MODULE_PREFIXES,
} from '../../lib/audit-actions'
import { usePageSize } from '../../lib/display-prefs'

/** Relative "N ago" reading for the Date and Time tooltip. Mirrors the
 *  Super Admin audit log so both surfaces phrase recency identically;
 *  anything older than a week falls back to the platform-wide
 *  DD/MM/YYYY absolute date. */
function fmtRelative(iso) {
  const d = new Date(iso)
  const diffMs = Date.now() - d.getTime()
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin} ${diffMin === 1 ? 'minute' : 'minutes'} ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH} ${diffH === 1 ? 'hour' : 'hours'} ago`
  const diffD = Math.round(diffH / 24)
  if (diffD < 7) return `${diffD} ${diffD === 1 ? 'day' : 'days'} ago`
  return formatDate(iso)
}

/** "previousStatus" / "next_status" → "Previous Status" / "Next Status".
 *  Splits camelCase and snake/dot case so a raw metadata key reads as a
 *  plain-English label in the details list. */
function humaniseMetaKey(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Render a SCALAR metadata value in plain English: booleans become
 *  Yes / No, empty values a dash, arrays a comma list (or "N items" for
 *  arrays of objects). Nested objects are expanded into sub-rows by
 *  `flattenMeta` instead of passing through here, so a raw JSON blob never
 *  reaches the UI. */
function formatMetaValue(value) {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (Array.isArray(value)) {
    if (value.length === 0) return '—'
    const allScalar = value.every((x) => x === null || typeof x !== 'object')
    return allScalar
      ? value.map((x) => formatMetaValue(x)).join(', ')
      : `${value.length} item${value.length === 1 ? '' : 's'}`
  }
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/** A plain, expandable object (not null / not an array). */
function isExpandableObject(v) {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
}

/** A `{ from, to }` change pair — rendered as "old → new" on one line
 *  rather than two separate rows. */
function isChangePair(v) {
  return (
    isExpandableObject(v) &&
    ('from' in v || 'to' in v) &&
    Object.keys(v).every((k) => k === 'from' || k === 'to')
  )
}

/** Flatten metadata into display rows, expanding nested objects into
 *  indented sub-rows and rendering `{from,to}` pairs as "old → new" — so
 *  the Details panel reads as a labelled list, never a raw JSON blob.
 *  Each row: { id, label, depth, group? , change? , value? }. */
function flattenMeta(obj, depth = 0, prefix = '') {
  const rows = []
  for (const [k, v] of Object.entries(obj)) {
    const id = prefix ? `${prefix}.${k}` : k
    const label = humaniseMetaKey(k)
    if (isChangePair(v)) {
      rows.push({
        id,
        label,
        depth,
        change: { from: formatMetaValue(v.from), to: formatMetaValue(v.to) },
      })
    } else if (isExpandableObject(v)) {
      const sub = Object.entries(v)
      if (sub.length === 0) {
        rows.push({ id, label, depth, value: '—' })
      } else {
        rows.push({ id, label, depth, group: true })
        rows.push(...flattenMeta(v, depth + 1, id))
      }
    } else {
      rows.push({ id, label, depth, value: formatMetaValue(v) })
    }
  }
  return rows
}

/**
 * /audit-log — Company-Admin-only audit log feed for the tenant.
 * Column layout mirrors the Super Admin Audit Logs page (SR. No. ·
 * Date and Time · Action · Description · User) so both audit surfaces
 * read identically — minus the Company column, which is redundant on
 * a single-tenant view. Cursor-paginated by `createdAt` via
 * `GET /admin/audit-log`.
 */
export function AuditLogPage() {
  const [search, setSearch] = useState('')
  // Cascading Module → Action filter. `moduleSel` is the module prefix
  // (`tasks`, `support`, …); `actionSel` is a specific dot-namespaced
  // key once one is picked under that module. Changing the module
  // clears the action.
  const [moduleSel, setModuleSel] = useState('')
  const [actionSel, setActionSel] = useState('')
  // ID of the row whose details modal is currently open. `null` = no
  // modal. Switched from an inline-row expansion to a portalled modal
  // so the table layout stays scannable and the gap between columns
  // doesn't grow when a row is opened.
  const [detailRowId, setDetailRowId] = useState(null)
  // Server-side pagination — page (1-based) + rows-per-page. Any filter
  // change resets to page 1 so the user never lands on an out-of-range page.
  const [page, setPage] = useState(1)
  // Starts at (and follows) the account rows-per-page (My Preferences);
  // this list's API accepts every standard size up to 500.
  const [limit, setLimit] = usePageSize()

  function changeModule(value) {
    setModuleSel(value)
    setActionSel('')
    setPage(1)
  }

  function changeSearch(value) {
    setSearch(value)
    setPage(1)
  }

  function changeAction(value) {
    setActionSel(value)
    setPage(1)
  }

  // Pagination handlers — a size change restarts at page 1; both close any
  // open detail modal so it can't point at a row that's scrolled away.
  function changePage(next) {
    setPage(Math.max(Number(next) || 1, 1))
    setDetailRowId(null)
  }
  function changeLimit(next) {
    setLimit(Number(next) || 50)
    setPage(1)
    setDetailRowId(null)
  }

  // A Clear chip that appears once any filter is active.
  const hasAnyFilter =
    search.trim() !== '' || moduleSel !== '' || actionSel !== ''

  function clearFilters() {
    setSearch('')
    setModuleSel('')
    setActionSel('')
    setPage(1)
  }

  // Shared query params for the active Search + Module → Action filter.
  // Used by both the paged table query and the export "all records" fetch
  // so a CSV always mirrors exactly what the filters show on screen.
  function filterParams() {
    const params = {}
    if (search.trim()) {
      params.search = search.trim()
      // The Action label + Description are humanized/curated client-side and
      // not stored, so a raw regex on the DB key can't match what the user
      // sees. Resolve the term to matching action KEYS here (we own the
      // catalogue) and send them so the backend can match `action ∈ keys`.
      const actionKeys = actionKeysMatching(search.trim(), { companyAdmin: true })
      if (actionKeys.length) params.actions = actionKeys.join(',')
    }
    // A specific Action (exact key) wins over the Module prefix; the
    // backend matches the full key exactly and the bare prefix as an
    // anchored prefix.
    if (actionSel) params.action = actionSel
    else if (moduleSel) params.action = moduleSel
    return params
  }

  const query = useQuery({
    queryKey: ['audit-log', moduleSel, actionSel, search, page, limit],
    queryFn: async () => {
      const res = await api.get('/admin/audit-log', {
        params: { ...filterParams(), page, limit },
      })
      return auditLogResponse.parse(res.data)
    },
    // Keep the current page visible while the next one loads.
    placeholderData: (prev) => prev,
  })

  const rawRows = query.data?.data ?? []
  const pageInfo = query.data?.pagination ?? null
  const detailRow = detailRowId
    ? rawRows.find((r) => r.id === detailRowId) ?? null
    : null

  // Export "all records" pages through the filtered feed (bounded to keep a
  // huge log from stalling the browser); "current page" and "selected" fall
  // back to the rows DataTable already holds.
  async function fetchAllForExport() {
    const all = []
    const exportLimit = 500
    for (let p = 1; p <= 50; p += 1) {
      const res = await api.get('/admin/audit-log', {
        params: { ...filterParams(), page: p, limit: exportLimit },
      })
      const parsed = auditLogResponse.parse(res.data)
      all.push(...(parsed.data ?? []))
      const info = parsed.pagination
      if (!info || p >= info.totalPages) break
    }
    return all
  }

  const columns = useMemo(
    () => [
      {
        key: 'createdAt',
        header: 'Date and Time',
        minWidth: 180,
        sortAccessor: (r) => (r.createdAt ? new Date(r.createdAt) : null),
        exportValue: (r) => formatDateTime(r.createdAt),
        cellClassName:
          'text-sm text-[color:var(--color-ink-muted)] tabular-nums whitespace-nowrap',
        render: (r) => (
          <span title={fmtRelative(r.createdAt)}>{formatDateTime(r.createdAt)}</span>
        ),
      },
      {
        key: 'action',
        header: 'Event',
        minWidth: 160,
        sortAccessor: (r) => actionLabel(r.action).toLowerCase(),
        exportValue: (r) => actionLabel(r.action),
        render: (r) => (
          <span
            className={
              'inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium border ' +
              actionToneClasses(r.action)
            }
            title={r.action}
          >
            {actionLabel(r.action)}
          </span>
        ),
      },
      {
        key: 'description',
        header: 'Description',
        minWidth: 300,
        grow: true,
        sortable: false,
        exportValue: (r) => describeAuditRow(r),
        render: (r) => (
          <span
            className="block truncate text-sm text-[color:var(--color-ink)]"
            title={describeAuditRow(r)}
          >
            {describeAuditRow(r)}
          </span>
        ),
      },
      {
        key: 'user',
        header: 'User',
        minWidth: 200,
        sortAccessor: (r) =>
          (r.actorName || (r.actorId ? '' : 'System')).toLowerCase(),
        exportValue: (r) =>
          r.actorName
            ? r.actorEmail
              ? `${r.actorName} (${r.actorEmail})`
              : r.actorName
            : r.actorId
              ? ''
              : 'System',
        render: (r) =>
          r.actorId ? (
            <div className="flex items-center gap-2.5 min-w-0">
              <Avatar name={r.actorName || ''} size="sm" />
              <div className="min-w-0">
                <p className="m-0 font-medium text-[color:var(--color-ink-strong)] truncate">
                  {r.actorName || '—'}
                </p>
                {r.actorEmail ? (
                  <p className="m-0 text-xs text-[color:var(--color-ink-muted)] truncate">
                    {r.actorEmail}
                  </p>
                ) : r.actorRole ? (
                  <p className="m-0 text-xs uppercase tracking-wider text-[color:var(--color-ink-muted)] truncate">
                    {r.actorRole}
                  </p>
                ) : null}
              </div>
            </div>
          ) : (
            // System-performed action (no actor) — show "System" to match the
            // detail popup's "Performed by: System" instead of a bare dash.
            <div className="flex items-center gap-2.5 min-w-0">
              <Avatar name="System" size="sm" />
              <p className="m-0 font-medium text-[color:var(--color-ink-muted)] truncate">
                System
              </p>
            </div>
          ),
      },
      {
        key: 'actions',
        header: 'Action',
        pinnedRight: true,
        hideable: false,
        sortable: false,
        exportable: false,
        align: 'center',
        minWidth: 72,
        render: (r) => (
          <RowActionsMenu
            items={[
              {
                key: 'view',
                label: 'View details',
                icon: Eye,
                onClick: () => setDetailRowId(r.id),
              },
            ]}
          />
        ),
      },
    ],
    [],
  )

  return (
    <div>
      <Breadcrumb current="Audit Log" />

      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--color-ink-strong)] flex items-center gap-3">
            <ShieldCheck
              size={22}
              className="text-[color:var(--color-accent-strong)]"
            />
            Audit Log
          </h1>
          <p className="text-sm text-[color:var(--color-ink-muted)] mt-1">
            {pageInfo && pageInfo.total > 0
              ? `${formatNumber(pageInfo.total)} ${
                  pageInfo.total === 1 ? 'entry' : 'entries'
                } · see what changed and who did it.`
              : 'See what changed and who did it.'}
          </p>
        </div>
      </div>

      {/* Filters — shared FilterRow primitive above the table card.
       *  The Module → Action pair cascades: Action stays disabled
       *  ("Select module first") until a Module is chosen. */}
      <FilterRow>
        <FilterRow.Search
          value={search}
          onChange={changeSearch}
          placeholder="Search Event or User"
        />
        <FilterRow.Select
          ariaLabel="Module"
          placeholder="All modules"
          value={moduleSel}
          onChange={changeModule}
          options={moduleOptions(CA_MODULE_PREFIXES)}
        />
        <FilterRow.Select
          ariaLabel="Event"
          placeholder={moduleSel ? 'All events' : 'Select module first'}
          value={actionSel}
          onChange={changeAction}
          options={actionOptions(moduleSel, { companyAdmin: true })}
          disabled={!moduleSel}
        />
        <FilterRow.Spacer />
        <FilterRow.Clear show={hasAnyFilter} onClick={clearFilters} />
      </FilterRow>

      {/* Scope note for the selected category — e.g. Authentication covers
       *  password / invitation events, not sign-ins (those are on Login
       *  Activity). Only shows when the chosen module has a note. */}
      {moduleDescription(moduleSel) ? (
        <p className="-mt-1 mb-3 text-xs text-[color:var(--color-ink-muted)]">
          {moduleDescription(moduleSel)}
        </p>
      ) : null}

      {/* Server-paginated log — Search + Module → Action filters live in the
       *  FilterRow above, so DataTable's own search box is suppressed. Sort
       *  reorders the current page (server returns newest-first). Row menu
       *  opens the same portalled details modal the whole row used to. */}
      <DataTable
        tableId="app-audit-log"
        columns={columns}
        rows={rawRows}
        getRowId={(r) => r.id}
        loading={query.isLoading}
        fetching={query.isFetching}
        error={query.isError ? extractApiError(query.error).message : null}
        onRetry={() => query.refetch()}
        searchable={false}
        defaultSort={[{ key: 'createdAt', dir: 'desc' }]}
        page={pageInfo?.page ?? page}
        totalPages={pageInfo?.totalPages ?? 1}
        total={pageInfo?.total ?? 0}
        limit={pageInfo?.limit ?? limit}
        onPageChange={changePage}
        onLimitChange={changeLimit}
        exportBaseName="audit-log"
        exportTitle="Audit Log"
        getExportRows={fetchAllForExport}
        emptyState={{
          icon: Activity,
          title: hasAnyFilter
            ? 'No audit events match your filters.'
            : 'No audit events recorded yet.',
        }}
      />

      {/* Details modal — opened by clicking any row. Portalled to body
       *  so the backdrop covers the full viewport regardless of where
       *  the page is scrolled to. */}
      {detailRow ? (
        <DetailsModal row={detailRow} onClose={() => setDetailRowId(null)} />
      ) : null}
    </div>
  )
}

/** Portalled modal that shows the same details payload that used to
 *  live in the in-row expansion. Outside-click and Escape both close
 *  it; clicking inside the panel doesn't bubble. */
function DetailsModal({ row, onClose }) {
  // Escape-to-close. `Esc` is the universal "back out" gesture — the
  // backdrop click handler covers mouse users.
  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  const meta =
    row.metadata && typeof row.metadata === 'object' ? row.metadata : {}
  const metaEntries = Object.entries(meta)

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl rounded-xl bg-[color:var(--color-card)] border border-[color:var(--color-rule)] shadow-2xl max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Audit event details"
      >
        <div className="px-5 py-3 border-b border-[color:var(--color-rule)] flex items-center justify-between bg-[color:var(--color-canvas)]">
          <h2 className="text-sm font-medium uppercase tracking-wider text-[color:var(--color-ink-muted)] m-0">
            Event Details
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded hover:bg-[color:var(--color-card-hover)] text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-ink-strong)] cursor-pointer"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto">
          {/* Plain-English summary line — the same description the table
           *  row shows, so the modal opens with a readable sentence
           *  rather than a raw action key. */}
          <p className="text-sm text-[color:var(--color-ink-strong)] leading-relaxed m-0 mb-4">
            {describeAuditRow(row)}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <DetailField label="Action" value={actionLabel(row.action)} />
            <DetailField
              label="Performed by"
              value={
                row.actorName
                  ? row.actorEmail
                    ? `${row.actorName} (${row.actorEmail})`
                    : row.actorName
                  : 'System'
              }
            />
            <DetailField
              label="Date and time"
              value={formatDateTime(row.createdAt)}
            />
            {row.actorRole ? (
              <DetailField label="Role" value={row.actorRole} />
            ) : null}
            {row.ipAddress ? (
              <DetailField label="IP address" value={row.ipAddress} />
            ) : null}
          </div>
          {/* Metadata as a humanised key–value list — "Previous status:
           *  suspended" rather than a raw JSON blob. */}
          {metaEntries.length > 0 ? (
            <div className="mt-4">
              <h3 className="text-sm uppercase tracking-wider font-medium text-[color:var(--color-ink-muted)] mb-1.5 mt-0">
                Details
              </h3>
              <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-canvas)] divide-y divide-[color:var(--color-rule)] overflow-hidden">
                {flattenMeta(meta).map((r) => (
                  <div
                    key={r.id}
                    className="flex items-start justify-between gap-4 px-3 py-2"
                    style={
                      r.depth ? { paddingLeft: 12 + r.depth * 16 } : undefined
                    }
                  >
                    <span
                      className={
                        'text-xs ' +
                        (r.group
                          ? 'font-semibold text-[color:var(--color-ink)]'
                          : 'text-[color:var(--color-ink-muted)]')
                      }
                    >
                      {r.label}
                    </span>
                    {r.group ? null : (
                      <span className="text-sm text-[color:var(--color-ink-strong)] text-right break-words">
                        {r.change ? (
                          <>
                            {r.change.from}{' '}
                            <span className="text-[color:var(--color-ink-muted)]">
                              →
                            </span>{' '}
                            {r.change.to}
                          </>
                        ) : (
                          r.value
                        )}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  )
}

function DetailField({ label, value }) {
  return (
    <div>
      <h3 className="text-sm uppercase tracking-wider font-semibold text-[color:var(--color-ink-muted)] m-0">
        {label}
      </h3>
      <p className="mt-0.5 text-sm text-[color:var(--color-ink-strong)] break-words">
        {value}
      </p>
    </div>
  )
}
